import React, { createContext, useContext, useEffect, useState } from "react";
import { useParams, Navigate, Outlet } from "react-router-dom";
import { useConvexAuth } from "convex/react";
import convex from "../convexClient";
import { api } from "../convex/_generated/api";
import { useTenantStore } from "../stores/useTenantStore";
import { resetWorkspaceScopedState } from "../utils/workspaceIsolation";

/* ────────────────────────────────────────────────────────────────────────
 * Context
 * ────────────────────────────────────────────────────────────────────── */
const TenantContext = createContext(null);

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantGuard");
  return ctx;
};

/* ────────────────────────────────────────────────────────────────────────
 * TenantGuard
 * Wraps all tenant-scoped routes (path: "/:tenantSlug/*").
 * – Verifies the slug exists and the current user is a member.
 * – Sets the active tenant in useTenantStore.
 * – Redirects to /sign-in if not authenticated.
 * ────────────────────────────────────────────────────────────────────── */
const TenantGuard = () => {
  const { tenantSlug } = useParams();
  const { isLoading, isAuthenticated } = useConvexAuth();
  const { setActiveTenant, tenantSlug: cachedSlug } = useTenantStore();
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "denied" | "notfound"
  const [tenant, setTenant] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setTenant(null);
      setRole(null);
      setStatus("denied");
      return;
    }

    let cancelled = false;

    if (cachedSlug !== tenantSlug) {
      resetWorkspaceScopedState();
    }

    setStatus("loading");
    setTenant(null);
    setRole(null);

    const resolve = async () => {
      try {
        const t = await convex.query(api.tenants.getTenantBySlug, { slug: tenantSlug });
        if (cancelled) return;
        if (!t) { setStatus("notfound"); return; }

        const memberRole = await convex.query(api.tenants.getMyRole, { tenantId: t.id });
        if (cancelled) return;
        if (!memberRole) { setStatus("denied"); return; }

        setTenant(t);
        setRole(memberRole);
        setActiveTenant({ ...t, role: memberRole });
        setStatus("ok");
      } catch (err) {
        console.error("[TenantGuard]", err);
        if (!cancelled) setStatus("denied");
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [tenantSlug, isAuthenticated, isLoading, setActiveTenant, cachedSlug]);

  const isResolvedForRoute = status === "ok" && tenant?.slug === tenantSlug;

  if (isLoading || status === "loading" || (status === "ok" && !isResolvedForRoute)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="w-8 h-8 border-[3px] border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (status === "denied") {
    return <Navigate to="/sign-in" replace />;
  }

  if (status === "notfound") {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <h2 className="text-xl font-semibold text-gray-800">Workspace not found</h2>
        <p className="text-sm text-gray-500">
          The workspace <strong>{tenantSlug}</strong> does not exist or you don't have access.
        </p>
        <button
          onClick={() => { window.location.href = "https://wiseup.africa"; }}
          className="px-4 py-2 bg-primary text-white rounded-md text-sm"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <TenantContext.Provider
      key={tenant?.id || tenantSlug}
      value={{ tenant, tenantSlug, tenantId: tenant?.id, role }}
    >
      <Outlet />
    </TenantContext.Provider>
  );
};

export default TenantGuard;
